<template>
  <ElFormItem
    :prop="name"
    :label="label"
    :nolabel="noLabel"
    :double="doubleWidth"
    class="fi-input"
  >
    <ElInput
      v-model="value"
      :max="max"
      :min="min"
      v-bind="$attrs"
      :clearable="clearable"
      class="clean-number-input"
      @change="(immediately ? noop : handleChange)($event)"
      @input="(immediately ? handleChange : noop)($event)"
    >
      <template v-if="help || $slots.suffix" #suffix>
        <HelpIcon v-if="help" :help="help" />
        <slot name="suffix" />
      </template>
    </ElInput>
  </ElFormItem>
</template>

<script lang="ts" setup>
/**
 * ### 文本输入组件
 * 传入未指定属性会透传到 ElInput 组件中
 */
import { ref, watch } from 'vue';
import { ElFormItem, ElInput } from 'element-plus';
import { useShareData } from '../../../core/SharedData';
import HelpIcon from '../../../common/HelpIcon.vue';
import { formatDecimal } from '../../../core/Common';
import { noop } from 'lodash-es';
import { getNumberLimit, editLimit } from '../../../core/InputNumberUtil';

const props = withDefaults(
  defineProps<{
    /**
     * ### post参数名
     */
    name: string;
    /**
     * ### 表单项名
     */
    label: string;
    /**
     * ### 默认值
     */
    defaultValue?: string;
    /**
     * ### 小数位数(0表示整数), undefined表示使用string值
     * <warning> 当存在该属性时数字大小在负一亿到正一亿之间
     * <warning> 只有当值为0时才会给后端数字类型 否则依旧为字符串类型（避免小数传输中精度丢失问题）
     * <warning> 输入科学计数法也会转成数字
     */
    decimal?: number;
    /**
     * ### 是否使用整数
     * <error> 已废弃 请使用decimal = 0代替
     */
    int?: boolean;
    /**
     * ### 组件是否使用双倍宽度
     */
    double?: boolean;
    /**
     * ### 帮助信息
     */
    help?: string;
    /**
     * ### 是否可清除
     */
    clearable?: boolean;
    /**
     * ### 是否在输入时立即触发值转换逻辑（数值转换/校验等）
     * true在input事件触发 false在change事件触发
     */
    immediately?: boolean;
  }>(),
  {
    clearable: true
  }
);

// eslint-disable-next-line vue/no-setup-props-destructure
const name = props.name;

// 在form中注册
useShareData('form', 'items').$(name)(true);

const model = useShareData('form', 'model');
if (model[name] === undefined) {
  model[name] = props.defaultValue ?? '';
}

const decimal =
  props.decimal !== undefined ? props.decimal : props.int ? 0 : undefined;

const { max, min } = getNumberLimit(decimal !== undefined);

function convert(v: any) {
  if (decimal === undefined) return v;
  if (v === undefined || v === '') return undefined;

  v = editLimit(v, max, min);

  const formatted = formatDecimal(v, decimal);

  // 整数转成number值, 否则保持string
  if (decimal == 0) return Number(formatted);

  return formatted;
}

const value = ref(convert(model[name]));
watch(
  () => model[name],
  (v) => {
    value.value = convert(v);
  }
);

function handleChange(newVal: any) {
  const formatted = convert(newVal);
  value.value = formatted;
  model[name] = formatted;
}

// 是否有label, 没有label 采用压缩格式显示
const noLabel = props.label ? undefined : '';

// 双倍宽度
const doubleWidth = props.double ? '' : undefined;
</script>

<style lang="scss" scoped>
.fi-input {
  //width: auto;
  height: var(--fi-line-height);

  --el-font-size-base: var(--fi-font-size);
  --el-input-icon-color: #8f959e;

  :deep(.el-input) {
    display: inline-block;
    width: auto;
  }

  :deep(.el-input.is-focus) {
    box-shadow: none;
  }

  :deep(.el-input.is-disabled .el-input__inner) {
    cursor: default !important;
  }

  :deep(.el-input__wrapper) {
    width: 100%;
    box-shadow: none;
    box-sizing: border-box;
    border-radius: var(--fi-border-radius);
    border: 1px solid var(--fi-border-color);
    height: calc(var(--fi-line-height) - 2px);
    padding-top: 0;
    padding-bottom: 0;
  }

  :deep(.el-input__wrapper:hover) {
    border-color: var(--fi-border-hover-color);
  }

  :deep(.el-input__prefix) {
    max-width: var(--fi-prefix-width);
    margin-right: var(--fi-prefix-right-space);
    padding-left: 12px;
    overflow: hidden;
  }

  :deep(.el-input__inner) {
    height: calc(var(--fi-line-height) - 2px);
    line-height: calc(var(--fi-line-height) - 2px);
  }

  .el-input + .el-icon {
    margin-left: 8px;
  }

  &[nolabel] {
    // display: inline-block;

    :deep(.el-form-item__content) {
      margin-left: 0 !important;

      :deep(.el-input) {
        width: 50px;
      }
    }
  }
}

.fi-input[double] {
  grid-column-start: span 2;
}
</style>
